// LocalStorage helpers for PopUp Profit V1
const KEYS = {
  events: "popupprofit:events",
  checklist: "popupprofit:checklist",
  lastCalc: "popupprofit:lastCalc",
  pro: "popupprofit:pro",
  earlyAccess: "popupprofit:earlyAccess",
};

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

// --- Pro flag (placeholder; payments not connected yet) ---
export function isPro() {
  return !!read(KEYS.pro, false);
}
export function setPro(v) {
  write(KEYS.pro, !!v);
}

// --- Early access list (stored locally; future migration to backend) ---
export function getEarlyAccess() {
  return read(KEYS.earlyAccess, []);
}
export function addEarlyAccess(email) {
  const list = getEarlyAccess();
  if (email && !list.includes(email)) {
    list.push({ email, joinedAt: new Date().toISOString() });
    write(KEYS.earlyAccess, list);
  }
  return list;
}

// --- Events ---
export const FREE_EVENT_LIMIT = 1;

export function getEvents() {
  return read(KEYS.events, []);
}
export function saveEvent(event) {
  const events = getEvents();
  const next = { ...event };
  if (next.id) {
    const idx = events.findIndex((e) => e.id === next.id);
    if (idx >= 0) events[idx] = next;
    else events.push(next);
  } else {
    next.id = `evt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    next.createdAt = new Date().toISOString();
    events.push(next);
  }
  write(KEYS.events, events);
  return next;
}
export function deleteEvent(id) {
  write(
    KEYS.events,
    getEvents().filter((e) => e.id !== id),
  );
}
export function getEvent(id) {
  return getEvents().find((e) => e.id === id) || null;
}
export function canAddEvent() {
  if (isPro()) return true;
  return getEvents().length < FREE_EVENT_LIMIT;
}

// --- Event report (mutates event.report) ---
export function saveEventReport(eventId, report) {
  const event = getEvent(eventId);
  if (!event) return null;
  event.report = {
    ...event.report,
    ...report,
    completed: true,
    completedAt: new Date().toISOString(),
  };
  saveEvent(event);
  return event;
}

// --- Checklist (per-event or global) ---
export function getChecklist(eventId = "global") {
  const all = read(KEYS.checklist, {});
  return all[eventId] || {};
}
export function setChecklistItem(eventId, item, checked) {
  const all = read(KEYS.checklist, {});
  if (!all[eventId]) all[eventId] = {};
  all[eventId][item] = checked;
  write(KEYS.checklist, all);
}
export function resetChecklist(eventId = "global") {
  const all = read(KEYS.checklist, {});
  delete all[eventId];
  write(KEYS.checklist, all);
}
export function getChecklistProgress(eventId = "global", total = 21) {
  const map = getChecklist(eventId);
  const done = Object.values(map).filter(Boolean).length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

// --- Last calculator state ---
export function getLastCalc() {
  return read(KEYS.lastCalc, null);
}
export function setLastCalc(state) {
  write(KEYS.lastCalc, state);
}

// --- Computation utils ---
const num = (v) => (Number.isFinite(+v) ? +v : 0);

export function computeProfit(inputs = {}) {
  const costs =
    num(inputs.boothFee) +
    num(inputs.travelCost) +
    num(inputs.inventoryCost) +
    num(inputs.suppliesCost) +
    num(inputs.foodCost) +
    num(inputs.marketingCost) +
    num(inputs.otherCost);
  const expectedSales = num(inputs.expectedSales);
  const avgSale = num(inputs.avgSale);
  const revenue = expectedSales * avgSale;
  const profit = revenue - costs;
  const breakEvenSales = avgSale > 0 ? Math.ceil(costs / avgSale) : 0;
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

  let verdict = "neutral";
  let message = "Enter your numbers to see a recommendation.";
  if (revenue > 0 || costs > 0) {
    if (profit < 0) {
      verdict = "bad";
      message = "Probably not worth it — this event may cost more than it earns.";
    } else if (margin < 25 || profit < costs * 0.25) {
      verdict = "risky";
      message = "Risky event — review your costs and expected sales.";
    } else {
      verdict = "good";
      message = "Great event — this looks worth considering.";
    }
  }
  return { costs, revenue, profit, breakEvenSales, margin, verdict, message };
}

export function computeActual(report = {}) {
  const costs =
    num(report.actualBoothCost) +
    num(report.actualTravelCost) +
    num(report.actualSuppliesCost);
  const revenue = num(report.actualRevenue);
  const profit = revenue - costs;
  const sales = num(report.actualSales);
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  return { costs, revenue, profit, sales, margin };
}

export function getEventEstimate(event) {
  return computeProfit({
    boothFee: event.boothFee,
    travelCost: event.travelCost,
    inventoryCost: event.inventoryCost,
    suppliesCost: event.suppliesCost,
    foodCost: event.foodCost,
    marketingCost: event.marketingCost,
    otherCost: event.otherCost,
    expectedSales: event.expectedSales,
    avgSale: event.avgSale,
  });
}

export function getEventActual(event) {
  if (!event.report?.completed) return null;
  return computeActual(event.report);
}

// Repeat verdict combines profit outcome + user's own answer
export function getRepeatVerdict(event) {
  const actual = getEventActual(event);
  if (!actual) return { key: "incomplete", label: "Post-event report not completed.", tone: "neutral" };
  const answer = event.report?.repeatChoice;
  if (answer === "no" || actual.profit < 0) {
    return { key: "not-worth", label: "Not worth repeating", tone: "bad" };
  }
  if (answer === "maybe" || actual.profit < 50) {
    return { key: "needs-review", label: "Needs review", tone: "risky" };
  }
  return { key: "worth", label: "Worth repeating", tone: "good" };
}

export function getEventSummary(event) {
  const estimate = getEventEstimate(event);
  const actual = getEventActual(event);
  const repeat = getRepeatVerdict(event);
  let diff = null;
  let goalHit = null;
  if (actual) {
    diff = actual.profit - estimate.profit;
    goalHit = actual.profit >= estimate.profit;
  }
  return { estimate, actual, repeat, diff, goalHit };
}

export const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);

export const formatCurrencyCompact = (n) => {
  const v = +n || 0;
  if (Math.abs(v) >= 1000) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 1, notation: "compact" }).format(v);
  }
  return formatCurrency(v);
};
